import { SOCKET_EVENT } from "@sendsar/protocol";
import type {
  OnlineUserIdsSnapshot,
  PresenceEvent,
  RoomIdPayload,
  RoomReadEvent,
  TypingEvent,
} from "@sendsar/protocol";
import { io, type Socket } from "socket.io-client";
import { SendsarError } from "./errors.js";
import { EventEmitter } from "./events.js";
import { RestClient } from "./rest.js";
import { socketOriginFromApiUrl } from "./url.js";
import { FileAccessCache, collectUploadIdsFromMessages } from "./file-access-cache.js";
import { uploadFileToStorage, type UploadFileParams, type UploadFileResult } from "./uploads.js";

const DEFAULT_CONNECT_TIMEOUT_MS = 30_000;
import type {
  ConnectOptions,
  SendsarEvent,
  SendsarEventMap,
  SendsarInitOptions,
  JoinRoomParams,
  ListMessagesParams,
  ListRoomsParams,
  Message,
  SendMessageParams,
  RegisterDeviceTokenParams,
  DeviceTokenRecord,
  ToggleReactionParams,
  TypingParams,
  UpdateMessageParams,
  PresignUploadParams,
  PresignUploadResult,
  FileAccessUrl,
  TenantStorageStats,
} from "./types.js";

export class SendsarClient extends EventEmitter {
  readonly apiUrl: string;
  private readonly rest: RestClient;
  private readonly fileAccessCache = new FileAccessCache();
  private socket: Socket | null = null;
  private connectedUserId: string | null = null;
  /** Rooms to re-join after `connect()` (e.g. token refresh). Cleared on `disconnect()`. */
  private readonly joinedRooms = new Map<string, string>();

  constructor(options: SendsarInitOptions) {
    super();
    this.apiUrl = options.apiUrl;
    this.rest = new RestClient(options.apiUrl);
  }

  get currentUserId(): string | null {
    return this.connectedUserId;
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Replace the session JWT (e.g. after your backend refreshes it).
   * Does not reconnect the WebSocket — call `connect()` again if the socket dropped.
   */
  updateSessionToken(token: string): void {
    this.rest.setSessionToken(token);
  }

  /**
   * Connect with a session JWT from your backend (`POST /v1/auth/token` via your API).
   * Call `joinRoom` after connect when opening a conversation.
   * Re-call `connect()` after refreshing the token so the WebSocket uses the new JWT.
   * Previously joined rooms are re-joined automatically after reconnect.
   */
  async connect(options: ConnectOptions): Promise<void> {
    if (this.socket) {
      await this.teardownSocket();
    }

    this.emit("connecting", { userId: options.userId });

    this.connectedUserId = options.userId;
    this.rest.setSessionToken(options.token);

    const origin = socketOriginFromApiUrl(this.apiUrl);
    const socket = io(origin, {
      auth: { token: options.token },
      transports: ["websocket", "polling"],
      autoConnect: true,
    });

    this.socket = socket;
    this.bindSocket(socket);

    const timeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;

    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        cleanup();
        socket.disconnect();
        this.socket = null;
        this.connectedUserId = null;
        this.rest.setSessionToken(null);
        const err = new SendsarError(
          `Sendsar WebSocket connect timed out after ${timeoutMs}ms`,
          0,
          "",
        );
        this.emit(SOCKET_EVENT.ERROR, { message: err.message });
        reject(err);
      }, timeoutMs);

      const onConnect = () => {
        cleanup();
        this.emit("connected", { userId: options.userId });
        resolve();
      };
      const onError = (payload: { message?: string }) => {
        cleanup();
        const message = payload?.message ?? "WebSocket connection failed";
        this.emit(SOCKET_EVENT.ERROR, { message });
        reject(new Error(message));
      };
      const cleanup = () => {
        clearTimeout(timeoutId);
        socket.off("connect", onConnect);
        socket.off(SOCKET_EVENT.ERROR, onError);
        socket.off("connect_error", onConnectError);
      };
      const onConnectError = (err: Error) => {
        cleanup();
        this.emit(SOCKET_EVENT.ERROR, { message: err.message });
        reject(err);
      };
      socket.on("connect", onConnect);
      socket.on(SOCKET_EVENT.ERROR, onError);
      socket.on("connect_error", onConnectError);
    });

    this.rejoinTrackedRooms();
  }

  async disconnect(): Promise<void> {
    await this.teardownSocket();
    this.connectedUserId = null;
    this.rest.setSessionToken(null);
    this.joinedRooms.clear();
    this.emit("disconnected", undefined!);
  }

  on<E extends SendsarEvent>(
    event: E,
    handler: (payload: SendsarEventMap[E]) => void,
  ): () => void {
    return super.on(event, handler);
  }

  /** List rooms for the connected user. Supports filters via `ListRoomsParams`. */
  async listRooms(params?: ListRoomsParams) {
    return this.rest.listRooms(params);
  }

  /**
   * Load messages for a room (newest first from API). Use `nextCursor` from the
   * response to paginate older messages.
   */
  async getMessages(roomId: string, params?: ListMessagesParams) {
    const response = await this.rest.listMessages(roomId, params);
    this.cacheAccessUrlsFromMessages(response.messages);
    return response;
  }

  /** Send a message. Pass `clientMessageId` for idempotent / optimistic UI dedupe. */
  async sendMessage(roomId: string, params: SendMessageParams): Promise<Message> {
    const senderId = params.senderId ?? this.requireUserId();
    const message = await this.rest.sendMessage(roomId, { ...params, senderId });
    this.cacheAccessUrlsFromMessages([message]);
    return message;
  }

  async presignUpload(params: PresignUploadParams): Promise<PresignUploadResult> {
    return this.rest.presignUpload(params);
  }

  async completeUpload(uploadId: string): Promise<void> {
    return this.rest.completeUpload(uploadId);
  }

  /** Cancel a presigned upload that was never completed (e.g. user cancelled mid-upload). */
  async abortUpload(uploadId: string): Promise<void> {
    return this.rest.abortUpload(uploadId);
  }

  /**
   * Temporary download/preview URL for a completed upload.
   * Results are cached until shortly before `accessUrlExpiresAt`.
   */
  async getFileAccessUrl(uploadId: string): Promise<FileAccessUrl> {
    const cached = this.fileAccessCache.get(uploadId);
    if (cached) {
      return { uploadId, ...cached };
    }
    const fresh = await this.rest.getFileAccessUrl(uploadId);
    this.fileAccessCache.set(uploadId, fresh);
    return fresh;
  }

  async batchFileAccessUrls(uploadIds: string[]): Promise<FileAccessUrl[]> {
    const missing = this.fileAccessCache.missingUploadIds(uploadIds);
    if (missing.length > 0) {
      const { urls } = await this.rest.batchFileAccessUrls(missing);
      this.fileAccessCache.setMany(urls);
    }
    return uploadIds
      .map((uploadId) => {
        const cached = this.fileAccessCache.get(uploadId);
        return cached ? { uploadId, ...cached } : null;
      })
      .filter((item): item is FileAccessUrl => item != null);
  }

  /**
   * Presign → PUT bytes to storage → complete. Pass `roomId` when the file will be sent in a room.
   */
  async uploadFile(params: UploadFileParams): Promise<UploadFileResult> {
    return uploadFileToStorage(this.rest, params);
  }

  /**
   * Upload a file then send it as a file message part in one call.
   * `roomId` is applied to both the upload (access checks) and the message.
   */
  async sendFileMessage(
    roomId: string,
    params: UploadFileParams & {
      clientMessageId?: string;
      parentMessageId?: string;
      senderId?: string;
    },
  ): Promise<Message> {
    const uploaded = await this.uploadFile({ ...params, roomId });
    const message = await this.sendMessage(roomId, {
      senderId: params.senderId,
      clientMessageId: params.clientMessageId,
      parentMessageId: params.parentMessageId,
      parts: [
        {
          type: "file",
          uploadId: uploaded.uploadId,
          mediaType: uploaded.mediaType,
          filename: uploaded.filename,
        },
      ],
    });
    return message;
  }

  /**
   * Fill missing `accessUrl` on file parts (e.g. after socket `new-message` events).
   * Returns new message objects with URLs merged in; also updates the internal cache.
   */
  async hydrateFileAccessUrls(messages: Message[]): Promise<Message[]> {
    const uploadIds = collectUploadIdsFromMessages(messages).filter((uploadId) => {
      return !this.fileAccessCache.get(uploadId);
    });
    if (uploadIds.length > 0) {
      await this.batchFileAccessUrls(uploadIds);
    }

    return messages.map((message) => this.hydrateMessageFileParts(message));
  }

  /**
   * Storage usage for the authenticated tenant. Requires `x-api-key` on the server.
   */
  async getTenantStorage(): Promise<TenantStorageStats> {
    return this.rest.getTenantStorage();
  }

  /**
   * Replace message parts (sender only). Emits `message-updated` on connected clients.
   * Returns the updated message — merge into local state by `id`.
   */
  async updateMessage(
    roomId: string,
    messageId: string,
    params: UpdateMessageParams,
  ): Promise<Message> {
    const userId = params.userId ?? this.requireUserId();
    return this.rest.updateMessage(roomId, messageId, { ...params, userId });
  }

  /**
   * Soft-delete a message (sender only). Clears parts server-side.
   * Emits `message-updated` with `deletedAt` set.
   */
  async deleteMessage(roomId: string, messageId: string, userId?: string): Promise<Message> {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.deleteMessage(roomId, messageId, resolvedUserId);
  }

  /**
   * Toggle an emoji reaction for the connected user (add if missing, remove if present).
   */
  async toggleReaction(
    roomId: string,
    messageId: string,
    params: ToggleReactionParams,
  ): Promise<Message> {
    const userId = params.userId ?? this.requireUserId();
    return this.rest.toggleReaction(roomId, messageId, { ...params, userId });
  }

  async markRoomRead(roomId: string, lastReadMessageId?: string, userId?: string) {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.markRoomRead(roomId, resolvedUserId, lastReadMessageId);
  }

  async markRoomDelivered(
    roomId: string,
    lastDeliveredMessageId?: string,
    userId?: string,
  ): Promise<{ ok: true; lastDeliveredAt: string; lastDeliveredMessageId: string | null }> {
    const resolvedUserId = userId ?? this.requireUserId();
    return this.rest.markRoomDelivered(roomId, resolvedUserId, lastDeliveredMessageId);
  }

  /**
   * Register a push token for the connected user (offline notifications).
   * Call after `connect()` when the user opts in (OneSignal subscription id, FCM token, etc.).
   */
  async registerDeviceToken(params: RegisterDeviceTokenParams): Promise<DeviceTokenRecord> {
    this.requireUserId();
    return this.rest.registerDeviceToken(params);
  }

  async unregisterDeviceToken(token: string): Promise<void> {
    this.requireUserId();
    return this.rest.unregisterDeviceToken(token);
  }

  joinRoom(params: JoinRoomParams): void {
    this.assertSocket();
    const userId = params.userId ?? this.requireUserId();
    this.joinedRooms.set(params.roomId, userId);
    this.socket!.emit(SOCKET_EVENT.JOIN_ROOM, { roomId: params.roomId, userId });
  }

  leaveRoom(roomId: string, userId?: string): void {
    if (!this.socket?.connected) return;
    this.joinedRooms.delete(roomId);
    const resolvedUserId = userId ?? this.connectedUserId ?? undefined;
    this.socket.emit(
      SOCKET_EVENT.LEAVE_ROOM,
      resolvedUserId ? { roomId, userId: resolvedUserId } : roomId,
    );
  }

  setTyping(params: TypingParams): void {
    if (!this.socket?.connected) return;
    const userId = params.userId ?? this.requireUserId();
    this.socket.emit(SOCKET_EVENT.TYPING, {
      roomId: params.roomId,
      userId,
      isTyping: params.isTyping,
    });
  }

  private requireUserId(): string {
    if (!this.connectedUserId) {
      throw new Error("Not connected — call connect() first");
    }
    return this.connectedUserId;
  }

  private assertSocket(): void {
    if (!this.socket?.connected) {
      throw new Error("Not connected — call connect() first");
    }
  }

  private teardownSocket(): Promise<void> {
    const socket = this.socket;
    if (!socket) return Promise.resolve();

    this.socket = null;
    return new Promise<void>((resolve) => {
      if (!socket.connected) {
        socket.removeAllListeners();
        resolve();
        return;
      }
      socket.once("disconnect", () => {
        socket.removeAllListeners();
        resolve();
      });
      socket.disconnect();
    });
  }

  /** Re-emit `join-room` for rooms tracked before a reconnect (token refresh). */
  private rejoinTrackedRooms(): void {
    if (!this.socket?.connected || this.joinedRooms.size === 0) return;
    for (const [roomId, userId] of this.joinedRooms) {
      this.socket.emit(SOCKET_EVENT.JOIN_ROOM, { roomId, userId });
    }
  }

  private cacheAccessUrlsFromMessages(messages: Message[]): void {
    const urls: Array<{ uploadId: string; accessUrl: string; accessUrlExpiresAt: string }> = [];
    for (const message of messages) {
      for (const part of message.parts) {
        if (
          part.type === "file" &&
          typeof part.uploadId === "string" &&
          typeof part.accessUrl === "string" &&
          typeof part.accessUrlExpiresAt === "string"
        ) {
          urls.push({
            uploadId: part.uploadId,
            accessUrl: part.accessUrl,
            accessUrlExpiresAt: part.accessUrlExpiresAt,
          });
        }
      }
      if (message.parentMessage?.parts) {
        for (const part of message.parentMessage.parts) {
          if (
            part.type === "file" &&
            typeof part.uploadId === "string" &&
            typeof part.accessUrl === "string" &&
            typeof part.accessUrlExpiresAt === "string"
          ) {
            urls.push({
              uploadId: part.uploadId,
              accessUrl: part.accessUrl,
              accessUrlExpiresAt: part.accessUrlExpiresAt,
            });
          }
        }
      }
    }
    if (urls.length > 0) {
      this.fileAccessCache.setMany(urls);
    }
  }

  private hydrateMessageFileParts(message: Message): Message {
    const hydrateParts = (parts: Message["parts"]) =>
      parts.map((part) => {
        if (part.type !== "file" || typeof part.uploadId !== "string") {
          return part;
        }
        if (typeof part.accessUrl === "string" && typeof part.accessUrlExpiresAt === "string") {
          return part;
        }
        const cached = this.fileAccessCache.get(part.uploadId);
        return cached
          ? {
              ...part,
              accessUrl: cached.accessUrl,
              accessUrlExpiresAt: cached.accessUrlExpiresAt,
            }
          : part;
      });

    return {
      ...message,
      parts: hydrateParts(message.parts),
      parentMessage: message.parentMessage
        ? {
            ...message.parentMessage,
            parts: hydrateParts(message.parentMessage.parts),
          }
        : message.parentMessage,
    };
  }

  private bindSocket(socket: Socket): void {
    socket.on(SOCKET_EVENT.ERROR, (payload: { message?: string }) => {
      this.emit(SOCKET_EVENT.ERROR, { message: payload?.message ?? "Unknown error" });
    });
    socket.on(SOCKET_EVENT.NEW_MESSAGE, (message: Message) => {
      this.cacheAccessUrlsFromMessages([message]);
      this.emit(SOCKET_EVENT.NEW_MESSAGE, message);
    });
    socket.on(SOCKET_EVENT.MESSAGE_UPDATED, (message: Message) => {
      this.cacheAccessUrlsFromMessages([message]);
      this.emit(SOCKET_EVENT.MESSAGE_UPDATED, message);
    });
    socket.on(SOCKET_EVENT.PRESENCE, (payload: PresenceEvent) => {
      this.emit(SOCKET_EVENT.PRESENCE, payload);
    });
    socket.on(SOCKET_EVENT.TENANT_PRESENCE, (payload: PresenceEvent) => {
      this.emit(SOCKET_EVENT.TENANT_PRESENCE, payload);
    });
    socket.on(SOCKET_EVENT.TENANT_PRESENCE_SNAPSHOT, (payload: OnlineUserIdsSnapshot) => {
      this.emit(SOCKET_EVENT.TENANT_PRESENCE_SNAPSHOT, payload);
    });
    socket.on(SOCKET_EVENT.PRESENCE_SNAPSHOT, (payload: OnlineUserIdsSnapshot) => {
      this.emit(SOCKET_EVENT.PRESENCE_SNAPSHOT, payload);
    });
    socket.on(SOCKET_EVENT.TYPING, (payload: TypingEvent) => {
      this.emit(SOCKET_EVENT.TYPING, payload);
    });
    socket.on(SOCKET_EVENT.ROOM_READ, (payload: RoomReadEvent) => {
      this.emit(SOCKET_EVENT.ROOM_READ, payload);
    });
    socket.on(SOCKET_EVENT.JOINED_ROOM, (payload: RoomIdPayload) => {
      this.emit(SOCKET_EVENT.JOINED_ROOM, payload);
    });
    socket.on(SOCKET_EVENT.LEFT_ROOM, (payload: RoomIdPayload) => {
      this.emit(SOCKET_EVENT.LEFT_ROOM, payload);
    });
    socket.on(SOCKET_EVENT.CALL_INVITE, (payload) => {
      this.emit(SOCKET_EVENT.CALL_INVITE, payload);
    });
    socket.on(SOCKET_EVENT.CALL_ACCEPTED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_ACCEPTED, payload);
    });
    socket.on(SOCKET_EVENT.CALL_DECLINED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_DECLINED, payload);
    });
    socket.on(SOCKET_EVENT.CALL_ENDED, (payload) => {
      this.emit(SOCKET_EVENT.CALL_ENDED, payload);
    });
  }
}
